import React from "react";
import { DollarSign, Plus, Trash2, Scale, FileText } from "lucide-react";
import type { SowFields } from "../../../../../utils/api/endpoints/sow";

interface CommercialTermsEditorProps {
  fields: SowFields;
  setField: <K extends keyof SowFields>(key: K, value: SowFields[K]) => void;
}

export const CommercialTermsEditor: React.FC<CommercialTermsEditorProps> = ({
  fields,
  setField
}) => {
  const generalTerms = fields.generalTerms || [];
  const specificTerms = fields.specificTerms || [];

  const handleUpdateGeneralTerm = (index: number, updates: { term?: string; details?: string }) => {
    const updated = [...generalTerms];
    updated[index] = { ...updated[index], ...updates };
    setField("generalTerms", updated);
  };

  const handleAddGeneralTerm = () => {
    setField("generalTerms", [
      ...generalTerms,
      { sn: String(generalTerms.length + 1), term: "New Term", details: "Term condition and description." }
    ]);
  };

  const handleRemoveGeneralTerm = (index: number) => {
    if (generalTerms.length <= 1) {
      alert("At least one general term row is required.");
      return;
    }
    const filtered = generalTerms.filter((_, idx) => idx !== index);
    setField("generalTerms", filtered);
  };

  const handleUpdateSpecificTerm = (index: number, updates: { term?: string; details?: string }) => {
    const updated = [...specificTerms];
    updated[index] = { ...updated[index], ...updates };
    setField("specificTerms", updated);
  };

  const handleAddSpecificTerm = () => {
    setField("specificTerms", [
      ...specificTerms,
      { sn: String(specificTerms.length + 1), term: "New Specific Term", details: "Engagement specific condition." }
    ]);
  };

  const handleRemoveSpecificTerm = (index: number) => {
    if (specificTerms.length <= 1) {
      alert("At least one specific term row is required.");
      return;
    }
    const filtered = specificTerms.filter((_, idx) => idx !== index);
    setField("specificTerms", filtered);
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-slate-200 pb-4">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
          <DollarSign size={16} className="text-ctb-blue" />
          Section 9: Commercial Terms & Legal Conditions
        </h3>
        <p className="text-xs text-slate-500 mt-1">
          Set the engagement pricing, currency, billing model, consultant effort days, and customize the legal terms tables.
        </p>
      </div>

      {/* Commercial Parameters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">Total Price</label>
          <input
            type="number"
            value={fields.price ?? ""}
            onChange={(e) => setField("price", e.target.value === "" ? null : Number(e.target.value))}
            placeholder="0"
            className="w-full text-xs font-bold text-slate-900 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">Currency</label>
          <select
            value={fields.currency}
            onChange={(e) => setField("currency", e.target.value as "NZD" | "USD")}
            className="w-full text-xs font-bold text-slate-800 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 cursor-pointer"
          >
            <option value="NZD">NZD ($)</option>
            <option value="USD">USD ($)</option>
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">Consultant Days</label>
          <input
            type="number"
            value={fields.consultantDays ?? ""}
            onChange={(e) => setField("consultantDays", e.target.value === "" ? null : Number(e.target.value))}
            placeholder="e.g. 5"
            className="w-full text-xs font-bold text-slate-900 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[11px] font-semibold text-slate-600">Engagement Model</label>
          <select
            value={fields.engagementModel}
            onChange={(e) => setField("engagementModel", e.target.value as "one-time" | "recurring" | "retainer")}
            className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800 cursor-pointer font-medium"
          >
            <option value="one-time">One-time Assessment</option>
            <option value="recurring">Continuous PTaaS / Recurring</option>
            <option value="retainer">Security Retainer</option>
          </select>
        </div>
      </div>

      {/* Payment Terms Narrative */}
      <div className="space-y-2 pt-2 border-t border-slate-200">
        <label className="text-xs font-bold text-slate-800">Payment Terms</label>
        <textarea
          rows={2}
          value={fields.paymentTerms || ""}
          onChange={(e) => {
            const val = e.target.value;
            setField("paymentTerms", val);
            if (fields.specificTerms && fields.specificTerms.length > 0) {
              const updatedSpecific = fields.specificTerms.map((t) =>
                t.term && /payment/i.test(t.term) ? { ...t, details: val } : t
              );
              setField("specificTerms", updatedSpecific);
            }
          }}
          className="w-full text-xs text-slate-800 border border-slate-300 rounded-xl p-3 focus:outline-none focus:ring-2 focus:ring-ctb-blue/20 focus:border-ctb-blue leading-relaxed font-sans"
        />
      </div>

      {/* Engagement Structure Table */}
      {(() => {
        const structure = fields.engagementStructure || [];

        const handleUpdateStructure = (
          idx: number,
          updates: { phase?: string; description?: string; duration?: string }
        ) => {
          const updated = [...structure];
          updated[idx] = { ...updated[idx], ...updates };
          setField("engagementStructure", updated);
        };

        const handleAddStructure = () => {
          setField("engagementStructure", [
            ...structure,
            { phase: "New Phase", description: "Phase description and scope.", duration: "1-2 business days" }
          ]);
        };

        const handleRemoveStructure = (idx: number) => {
          if (structure.length <= 1) {
            alert("At least one engagement structure row is required.");
            return;
          }
          setField("engagementStructure", structure.filter((_, i) => i !== idx));
        };

        return (
          <div className="space-y-3 pt-2 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                  <FileText size={13} className="text-ctb-blue" />
                  Engagement Structure Table
                </label>
                <p className="text-[11px] text-slate-500">Phases, descriptions, and indicative durations for the engagement.</p>
              </div>
              <button
                type="button"
                onClick={handleAddStructure}
                className="px-2.5 py-1.5 bg-slate-800 text-white text-xs font-bold rounded-lg hover:bg-slate-900 flex items-center gap-1 cursor-pointer transition shadow-sm"
              >
                <Plus size={13} />
                Add Phase
              </button>
            </div>

            <div className="space-y-2">
              {structure.map((row, idx) => (
                <div
                  key={idx}
                  className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3 hover:border-slate-300 transition"
                >
                  <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 mt-1">
                    {idx + 1}
                  </span>
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-2">
                    <div className="md:col-span-3">
                      <input
                        type="text"
                        value={row.phase}
                        onChange={(e) => handleUpdateStructure(idx, { phase: e.target.value })}
                        placeholder="Phase Name"
                        className="w-full text-xs font-semibold bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
                      />
                    </div>
                    <div className="md:col-span-6">
                      <input
                        type="text"
                        value={row.description}
                        onChange={(e) => handleUpdateStructure(idx, { description: e.target.value })}
                        placeholder="Phase Description"
                        className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
                      />
                    </div>
                    <div className="md:col-span-3">
                      <input
                        type="text"
                        value={row.duration}
                        onChange={(e) => handleUpdateStructure(idx, { duration: e.target.value })}
                        placeholder="Duration"
                        className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveStructure(idx)}
                    disabled={structure.length <= 1}
                    className="text-slate-400 hover:text-rose-600 p-1 rounded-md transition disabled:opacity-30 cursor-pointer shrink-0 mt-1"
                    title="Remove Phase"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* General Terms Table */}
      <div className="space-y-3 pt-2 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
              <Scale size={13} className="text-ctb-blue" />
              General Commercial Terms Table
            </label>
            <p className="text-[11px] text-slate-500">Standard business and contractual terms.</p>
          </div>
          <button
            type="button"
            onClick={handleAddGeneralTerm}
            className="px-2.5 py-1.5 bg-slate-800 text-white text-xs font-bold rounded-lg hover:bg-slate-900 flex items-center gap-1 cursor-pointer transition shadow-sm"
          >
            <Plus size={13} />
            Add Term
          </button>
        </div>

        <div className="space-y-2">
          {generalTerms.map((row, idx) => (
            <div
              key={idx}
              className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3 hover:border-slate-300 transition"
            >
              <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 mt-1">
                {idx + 1}
              </span>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="md:col-span-4">
                  <input
                    type="text"
                    value={row.term}
                    onChange={(e) => handleUpdateGeneralTerm(idx, { term: e.target.value })}
                    placeholder="Term Name"
                    className="w-full text-xs font-semibold bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
                  />
                </div>
                <div className="md:col-span-8">
                  <input
                    type="text"
                    value={row.details}
                    onChange={(e) => handleUpdateGeneralTerm(idx, { details: e.target.value })}
                    placeholder="Term Details"
                    className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveGeneralTerm(idx)}
                disabled={generalTerms.length <= 1}
                className="text-slate-400 hover:text-rose-600 p-1 rounded-md transition disabled:opacity-30 cursor-pointer shrink-0 mt-1"
                title="Remove Term"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Specific Terms Table */}
      <div className="space-y-3 pt-2 border-t border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
              <FileText size={13} className="text-ctb-blue" />
              Specific Terms Table
            </label>
            <p className="text-[11px] text-slate-500">Project-specific clauses, retesting rules, and payment milestones.</p>
          </div>
          <button
            type="button"
            onClick={handleAddSpecificTerm}
            className="px-2.5 py-1.5 bg-slate-800 text-white text-xs font-bold rounded-lg hover:bg-slate-900 flex items-center gap-1 cursor-pointer transition shadow-sm"
          >
            <Plus size={13} />
            Add Term
          </button>
        </div>

        <div className="space-y-2">
          {specificTerms.map((row, idx) => (
            <div
              key={idx}
              className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-start gap-3 hover:border-slate-300 transition"
            >
              <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-700 text-[10px] font-bold flex items-center justify-center shrink-0 mt-1">
                {idx + 1}
              </span>
              <div className="flex-1 grid grid-cols-1 md:grid-cols-12 gap-2">
                <div className="md:col-span-4">
                  <input
                    type="text"
                    value={row.term}
                    onChange={(e) => handleUpdateSpecificTerm(idx, { term: e.target.value })}
                    placeholder="Term Name"
                    className="w-full text-xs font-semibold bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
                  />
                </div>
                <div className="md:col-span-8">
                  <input
                    type="text"
                    value={row.details}
                    onChange={(e) => handleUpdateSpecificTerm(idx, { details: e.target.value })}
                    placeholder="Term Details"
                    className="w-full text-xs bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 text-slate-800"
                  />
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRemoveSpecificTerm(idx)}
                disabled={specificTerms.length <= 1}
                className="text-slate-400 hover:text-rose-600 p-1 rounded-md transition disabled:opacity-30 cursor-pointer shrink-0 mt-1"
                title="Remove Term"
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
